import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Phone, Mail, MapPin, ImagePlus, X, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLines, FadeUp, ChapterLabel } from "@/components/Reveal";
import { API, useSiteData, defaultSettings, telHref } from "@/api";

const PRESTATIONS = [
  "Peinture intérieure décorative",
  "Peinture extérieure",
  "Ravalement de façade",
  "Pose de papier peint",
  "Enduits & préparation",
  "Autre",
];

const inputCls =
  "w-full border-b border-bark/40 bg-transparent py-3 text-ink placeholder:text-taupe/70 outline-none transition-[border-color] duration-300 focus:border-b-2 focus:border-clay";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="text-xs uppercase tracking-[0.25em] text-taupe">
      {label}
    </label>
    <div className="mt-2">{children}</div>
  </div>
);

const Contact = () => {
  const fileRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const site = useSiteData({ chantiers: null, services: null, settings: defaultSettings });
  const s = site.settings || defaultSettings;
  const [form, setForm] = useState({
    nom: "",
    telephone: "",
    email: "",
    ville: "",
    prestation: PRESTATIONS[0],
    description: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPhotos = (files) => {
    const next = [...photos, ...Array.from(files)].slice(0, 5);
    setPhotos(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      photos.forEach((p) => data.append("photos", p));
      await axios.post(`${API}/devis`, data);
      toast.success("Demande envoyée", {
        description: "Alexis a bien reçu votre demande et vous recontacte rapidement.",
      });
      setForm({ nom: "", telephone: "", email: "", ville: "", prestation: PRESTATIONS[0], description: "" });
      setPhotos([]);
    } catch (err) {
      toast.error("Envoi impossible", {
        description: err.response?.data?.detail || "Vérifiez les champs et réessayez, ou appelez directement Alexis.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact & devis gratuit — Peintre à Angoulême & Cognac | Alexis Peinture"
        description="Demandez votre devis gratuit de peinture intérieure décorative dans le Grand Angoulême et le Grand Cognac. Joignez vos photos pour un chiffrage à distance."
      />

      <section className="px-6 pb-16 pt-40 lg:px-12 lg:pt-48">
        <div className="mx-auto max-w-7xl">
          <ChapterLabel number="01" label="Contact" />
          <h1
            className="mt-8 max-w-4xl font-serif text-5xl leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
            data-testid="contact-title"
          >
            <MaskedLines
              lines={[
                "Parlons de",
                <span key="l2">
                  votre <em className="italic text-clay">projet.</em>
                </span>,
              ]}
            />
          </h1>
          <FadeUp delay={0.2} className="mt-8 max-w-xl">
            <p className="leading-relaxed text-bark">
              Décrivez votre besoin et joignez des photos de votre chantier :
              Alexis peut chiffrer à distance et vous rappelle pour affiner.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-32 lg:px-12">
        <div className="grid gap-20 lg:grid-cols-5">
          <FadeUp className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-10" data-testid="devis-form">
              <div className="grid gap-10 sm:grid-cols-2">
                <Field label="Nom" htmlFor="nom">
                  <input
                    id="nom"
                    data-testid="devis-input-nom"
                    required
                    value={form.nom}
                    onChange={set("nom")}
                    placeholder="Votre nom"
                    className={inputCls}
                  />
                </Field>
                <Field label="Téléphone" htmlFor="telephone">
                  <input
                    id="telephone"
                    data-testid="devis-input-telephone"
                    required
                    type="tel"
                    value={form.telephone}
                    onChange={set("telephone")}
                    placeholder="06 00 00 00 00"
                    className={inputCls}
                  />
                </Field>
                <Field label="Email" htmlFor="email">
                  <input
                    id="email"
                    data-testid="devis-input-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="vous@exemple.fr"
                    className={inputCls}
                  />
                </Field>
                <Field label="Ville" htmlFor="ville">
                  <input
                    id="ville"
                    data-testid="devis-input-ville"
                    required
                    value={form.ville}
                    onChange={set("ville")}
                    placeholder="Angoulême, Cognac…"
                    className={inputCls}
                  />
                </Field>
              </div>

              <Field label="Type de prestation" htmlFor="prestation">
                <select
                  id="prestation"
                  data-testid="devis-select-prestation"
                  value={form.prestation}
                  onChange={set("prestation")}
                  className={`${inputCls} cursor-pointer`}
                >
                  {PRESTATIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Votre besoin" htmlFor="description">
                <textarea
                  id="description"
                  data-testid="devis-input-description"
                  required
                  rows={5}
                  value={form.description}
                  onChange={set("description")}
                  placeholder="Décrivez les pièces concernées, l'état des murs, vos envies de couleurs…"
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-taupe">
                  Photos du chantier <span className="normal-case tracking-normal">(optionnel, 5 max — 4 Mo chacune)</span>
                </p>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  data-testid="devis-input-photos"
                  onChange={(e) => {
                    addPhotos(e.target.files);
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  data-testid="devis-photos-button"
                  onClick={() => fileRef.current?.click()}
                  className="mt-4 flex items-center gap-3 border border-dashed border-bark/40 px-6 py-4 text-sm text-bark transition-colors duration-300 hover:border-clay hover:text-clay"
                >
                  <ImagePlus size={17} /> Joindre des photos pour un chiffrage à distance
                </button>
                {photos.length > 0 && (
                  <ul className="mt-4 flex flex-wrap gap-2" data-testid="devis-photos-list">
                    {photos.map((p, i) => (
                      <li
                        key={`${p.name}-${i}`}
                        className="flex items-center gap-2 rounded-full bg-plaster px-4 py-2 text-xs text-bark"
                      >
                        {p.name}
                        <button
                          type="button"
                          data-testid={`devis-photo-remove-${i}`}
                          onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                          className="text-taupe transition-colors duration-200 hover:text-clay"
                          aria-label={`Retirer ${p.name}`}
                        >
                          <X size={13} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="devis-submit-button"
                className="group flex items-center gap-3 rounded-full bg-clay px-10 py-5 text-sm font-medium text-cream transition-[background-color,transform,opacity] duration-300 hover:scale-[1.03] hover:bg-ink disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? (
                  <>
                    Envoi en cours <Loader2 size={16} className="animate-spin" />
                  </>
                ) : (
                  <>
                    Envoyer ma demande de devis
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </FadeUp>

          <FadeUp delay={0.15} className="lg:col-span-2">
            <aside className="bg-ink p-10 text-cream lg:p-12" data-testid="contact-infos">
              <p className="text-xs uppercase tracking-[0.3em] text-sand">En direct</p>
              <h2 className="mt-5 font-serif text-3xl tracking-tight">{s.gerant}</h2>
              <div className="mt-10 space-y-7 text-sm">
                <a
                  href={telHref(s.telephone)}
                  data-testid="contact-phone"
                  className="flex items-start gap-4 transition-colors duration-300 hover:text-sand"
                >
                  <Phone size={17} className="mt-0.5 shrink-0 text-clay" />
                  <span>
                    <span className="block text-xs uppercase tracking-[0.2em] text-cream/50">Téléphone</span>
                    <span className="mt-1 block text-base">{s.telephone}</span>
                  </span>
                </a>
                <a
                  href={`mailto:${s.email}`}
                  data-testid="contact-email"
                  className="flex items-start gap-4 transition-colors duration-300 hover:text-sand"
                >
                  <Mail size={17} className="mt-0.5 shrink-0 text-clay" />
                  <span>
                    <span className="block text-xs uppercase tracking-[0.2em] text-cream/50">Email</span>
                    <span className="mt-1 block text-base break-all">{s.email}</span>
                  </span>
                </a>
                <div className="flex items-start gap-4">
                  <MapPin size={17} className="mt-0.5 shrink-0 text-clay" />
                  <span>
                    <span className="block text-xs uppercase tracking-[0.2em] text-cream/50">Zone d'intervention</span>
                    <span className="mt-1 block text-base leading-relaxed" data-testid="contact-zone">
                      {s.zone}
                    </span>
                  </span>
                </div>
              </div>
              <div className="mt-12 border-t border-cream/15 pt-8">
                <p className="text-sm leading-relaxed text-cream/70">
                  Devis gratuit et sans engagement. Réponse personnelle
                  d'Alexis, généralement sous 48 h ouvrées.
                </p>
              </div>
            </aside>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Contact;
